// components
import React from 'react';
import { MenuItem } from '@mui/material';

import GridPageAbstract from '../abstract/GridPageAbstract';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import CreateTransactionDialog from '../dialogs/CreateTransactionDialog';
import Iconify from '../../components/iconify';
import i18n from '../../i18n';
import { APPEAL_KIND } from '../../constants/appealKind';
import { APPEAL_STATUS } from '../../constants/appealStatus';
import { APPEAL_CORRECT_STATE, appealStatesFor } from '../../constants/appealCorrectState';

// Must match action-permissions.yml and co.itfed.spring.api.utils.ActionPermissions.
// Approve and reject are separate permissions: approving lets core-cron change a
// decided transaction's outcome and amount, rejecting only closes the request.
const APPROVE_ACTION = 'appeal-approve';
const REJECT_ACTION = 'appeal-reject';

export default class AppealsPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'publicAppeals';
        this.Server = this.props.context.getState().server.publicAppeals;
        this.MenuDisplayType = 'menu';
        this.searchFields = ['reason', 'description'];

        const defaultHead = [
            {
                id: 'kind', required: true,
                label: 'kind',
                alignRight: false,
                cell: 'Items',
                items: APPEAL_KIND,
                displayGrid: true
            },
            {
                id: 'payinId',
                label: 'payin',
                alignRight: false,
                cell: 'Select',
                from: 'payinInstances',
                object: 'payin',
                show: 'uuid',
                displayGrid: true
            },
            {
                id: 'payoutId',
                label: 'payout',
                alignRight: false,
                cell: 'Select',
                from: 'payoutInstances',
                object: 'payout',
                show: 'uuid',
                displayGrid: true
            },
            { id: 'correctValue', label: 'correctValue', alignRight: true, cell: 'Amount', displayGrid: true },
            {
                id: 'correctState',
                label: 'correctStatus',
                alignRight: false,
                cell: 'Items',
                items: APPEAL_CORRECT_STATE,
                // The editor offers only the states the chosen kind may end in.
                itemsFor: (data) => appealStatesFor(data.kind),
                displayGrid: true
            },
            // Filed pending; it moves only through Approve / Reject and core-cron.
            {
                id: 'status',
                label: 'status',
                alignRight: false,
                cell: 'State',
                items: APPEAL_STATUS,
                displayGrid: true,
                disableEditor: true
            },
            { id: 'reason', label: 'reason', alignRight: false, cell: 'Text', displayGrid: true },
            { id: 'description', label: 'description', alignRight: false, cell: 'BigText', displayGrid: true },
            // The Telegram message whose amount it credits, filed from the message's
            // row; fixed once filed, so never edited here.
            {
                id: 'tgSessionMessageId',
                label: 'tgSessionMessageId',
                alignRight: false,
                cell: 'Number',
                prefix: '#',
                blankWhenNull: true,
                displayGrid: true,
                disableEditor: true
            },
            // Written by core-cron when it applies an approved appeal, or fails to.
            { id: 'executedAt', label: 'executedAt', alignRight: false, cell: 'DateTime', displayGrid: true, disableEditor: true },
            { id: 'executionError', label: 'executionError', alignRight: false, cell: 'BigText', displayGrid: true, disableEditor: true },
            {
                id: 'modifiedReason',
                label: 'modifiedReason',
                alignRight: false,
                cell: 'BigText',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'modifiedBy',
                label: 'modifiedBy',
                alignRight: false,
                cell: 'Select',
                object: 'modifiedBy',
                show: 'firstName+lastName',
                displayGrid: true,
                disableEditor: true,
                customSort: 'modifiedBy.firstName'
            },
            {
                id: 'dateCreatedModified',
                elements: ['dateCreated', 'dateModified'],
                label: 'dateCreatedModified',
                alignRight: false,
                cell: 'CreateModify',
                disableEditor: true,
                displayGrid: true,
                customSort: 'dateCreated'
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head,
            decisionAction: null,
            decisionId: null,
            decisionPayout: null
        };
    }

    openDecision(action) {
        const id = this.state.menuClickItemId;
        this.handleCloseMenu();
        this.setState({ decisionAction: action, decisionId: id, decisionPayout: null });
        const row = this.List.find((item) => item.id === id);
        if (action === 'approve' && row?.kind === 'payout' && row?.correctState === 'withdrawed' && row?.payout?.id) {
            this.loadDecisionPayout(id, row.payout.id);
        }
    }

    /**
     * The payout an approval makes withdrawed, for whether it has a card yet: one of a
     * gateway that names the card at confirmation has none, and the approval names it.
     */
    loadDecisionPayout = async (appealId, payoutId) => {
        try {
            // 5: the list API's smallest page.
            const res = await this.props.context.getState().server.payoutInstances
                .search(5, [], [], [{ field: 'id', op: '=', value: payoutId }]);
            const payout = res?.error === 'OK' ? res.data?.find((row) => row.id === payoutId) : null;
            if (payout && this.state.decisionId === appealId) {
                this.setState({ decisionPayout: payout });
            }
        } catch (error) {
            console.error('Error loading the appealed payout:', error);
        }
    };

    closeDecision = () => {
        this.setState({ decisionAction: null, decisionId: null, decisionPayout: null });
        // The appeal's status changed, so the grid behind the dialog is now stale.
        this.retrieveData();
    };

    decided = () => {
        const approved = this.state.decisionAction === 'approve';
        this.props.context.update({
            alertError: {
                message: i18n.t(approved ? 'text.appealApproved' : 'text.appealRejected'),
                type: 'snackbar',
                severity: 'success'
            }
        });
    };

    /**
     * Only a pending appeal is still a request: once approved, rejected, executed or
     * failed it is a record, and the API refuses to edit or delete it.
     */
    rowEditable(row) {
        return row?.status === 'pending';
    }

    rowDeletable(row) {
        return row?.status === 'pending';
    }

    renderCustomMenuItems(id) {
        const row = this.List.find((item) => item.id === id);
        // Only a pending appeal is decided — the API refuses any other, and
        // offering the action would only produce an error.
        if (row?.status !== 'pending') return [];

        const items = [];
        if (this.allowedAction(APPROVE_ACTION)) {
            items.push(
                <MenuItem key={`${id}-approve`} onClick={() => this.openDecision('approve')}>
                    <Iconify icon="eva:checkmark-circle-2-outline" sx={{ mr: 2 }} />
                    {i18n.t('buttons.approveAppeal')}
                </MenuItem>
            );
        }
        if (this.allowedAction(REJECT_ACTION)) {
            items.push(
                <MenuItem key={`${id}-reject`} onClick={() => this.openDecision('reject')}>
                    <Iconify icon="eva:close-circle-outline" sx={{ mr: 2 }} />
                    {i18n.t('buttons.rejectAppeal')}
                </MenuItem>
            );
        }
        return items;
    }

    renderCustom() {
        const { decisionAction, decisionId } = this.state;
        if (!decisionAction || !decisionId) return null;

        const isApprove = decisionAction === 'approve';
        const row = this.List.find((item) => item.id === decisionId);
        const payout = this.state.decisionPayout;
        // The decision itself is the endpoint; the text is kept as the appeal's
        // modified reason. An approval making a payout with no card yet withdrawed
        // names the card its money left from — unless the appeal's Telegram message
        // names it, which the backend then takes.
        const head = [
            {
                id: 'text',
                label: isApprove ? 'note' : 'reason',
                cell: 'BigText',
                required: true,
                helperText: isApprove ? 'appealApproveNote' : 'appealRejectReason'
            }
        ];
        if (isApprove && payout && !payout.wallet) {
            head.push({
                id: 'walletId',
                label: 'wallet',
                cell: 'ServerSearch',
                required: !row?.tgSessionMessageId,
                searchController: 'publicWallets',
                // None: a uuid matches only whole, so the gateway's live cards are
                // fetched by the prefilter alone and narrowed here as the operator types.
                searchFields: [],
                show: 'uuid',
                limit: 100,
                sort: [{ field: 'id', direction: 'ASC' }],
                prefilter: [
                    { field: 'gateway.id', op: '=', value: payout.gateway?.id },
                    { field: 'isArchived', op: '=', value: false }
                ],
                helperText: 'appealApproveWallet'
            });
        }

        return (
            <CreateTransactionDialog
                dialogOpen
                // Keyed by the fields, so the card picker appears once the payout is read.
                key={`appeal-${decisionAction}-${decisionId}-${head.length}`}
                type={`appeal-${decisionAction}`}
                id={decisionId}
                Head={head}
                dialogTitle={isApprove ? 'dialogs.approveAppeal' : 'dialogs.rejectAppeal'}
                saveLabel={isApprove ? 'buttons.approveAppeal' : 'buttons.rejectAppeal'}
                enablePrevData={false}
                server={(id, body) =>
                    isApprove
                        ? this.Server.approve(id, body.text, body.walletId ?? undefined)
                        : this.Server.reject(id, body.text)
                }
                onSuccess={this.decided}
                onClose={this.closeDecision}
            />
        );
    }
}
